import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useToasts } from 'common/toast';
import { StyledButton } from 'common/components/button/StyledButton';
import { AppConfig } from '../../../Config';
import { authenticatedFetch } from '../../../common/utils/authFetch';
import { getCurrentOrganizationId } from '../../../common/selectors/auth';

interface DownloadCSVButtonProps {
  assessmentPeriodId?: number;
  filename?: string;
  disabled?: boolean;
}

const DownloadCSVButton: React.FC<DownloadCSVButtonProps> = ({
  assessmentPeriodId,
  filename = 'survey_export.csv',
  disabled = false,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { addToast } = useToasts();
  const organizationId = useSelector(getCurrentOrganizationId);

  const handleDownload = async () => {
    if (!assessmentPeriodId || !organizationId || isDownloading) {
      return;
    }

    setIsDownloading(true);

    try {
      const url = `${AppConfig.api.apiUrl}/api/portal/v1/assessments/survey-export/?assessment_period_id=${assessmentPeriodId}`;
      const response = await authenticatedFetch(url, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'X-NULOGIC-ORGANIZATION-ID': organizationId.toString(),
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to download survey export: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      addToast('Survey export downloaded successfully', {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch (error) {
      console.error('Failed to download survey export:', error);
      addToast('Failed to download survey export', {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <StyledButton
      onClick={handleDownload}
      disabled={disabled || isDownloading}
      label={isDownloading ? 'Downloading...' : 'Download CSV'}
      color="blue"
    />
  );
};

export default DownloadCSVButton;
